import type { CgDesignerAnimation } from '../../types/index.js'

/**
 * Temps d'affichage d'un element du Studio, compte depuis l'apparition de
 * l'habillage. Miroir de `normalizeElementTiming` et du runtime genere
 * (`backend/src/services/cg-template-designer.service.ts`) : l'element entre a
 * `delayMs`, reste `visibleForMs`, puis revient toutes les `repeatEveryMs`.
 */
export interface ElementTiming {
  delayMs?: number
  visibleForMs?: number
  repeatEveryMs?: number
  exitAnimation?: CgDesignerAnimation
  exitDurationMs?: number
}

export interface VisibilityWindow { from: number; to: number }

const DAY_MS = 86_400_000

function clampMs(value: number | undefined): number {
  const number = Number(value)
  return Number.isFinite(number) ? Math.min(DAY_MS, Math.max(0, Math.round(number))) : 0
}

export function normalizeElementTiming(element: ElementTiming): { visibleForMs: number; repeatEveryMs: number } {
  const visibleForMs = clampMs(element.visibleForMs)
  if (visibleForMs === 0) return { visibleForMs: 0, repeatEveryMs: 0 }
  const requested = clampMs(element.repeatEveryMs)
  if (requested === 0) return { visibleForMs, repeatEveryMs: 0 }
  const exitMs = element.exitAnimation === 'NONE' ? 0 : Math.max(1, clampMs(element.exitDurationMs ?? 300))
  return { visibleForMs, repeatEveryMs: Math.max(requested, visibleForMs + exitMs) }
}

const secondsFormat = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 })
const seconds = (ms: number) => `${secondsFormat.format(ms / 1000)} s`

export function timingSummary(element: ElementTiming): string {
  const delay = clampMs(element.delayMs)
  const { visibleForMs, repeatEveryMs } = normalizeElementTiming(element)
  if (!visibleForMs) return `Visible dès ${seconds(delay)}, jusqu'au retrait de l'habillage.`
  const window = `Visible de ${seconds(delay)} à ${seconds(delay + visibleForMs)}`
  return repeatEveryMs
    ? `${window}, puis ${seconds(visibleForMs)} toutes les ${seconds(repeatEveryMs)}.`
    : `${window}, une seule fois.`
}

/** Fenetres de visibilite jusqu'a `horizonMs` (timeline du Studio). */
export function visibilityWindows(element: ElementTiming, horizonMs: number): VisibilityWindow[] {
  const delay = clampMs(element.delayMs)
  const { visibleForMs, repeatEveryMs } = normalizeElementTiming(element)
  if (!visibleForMs) return delay < horizonMs ? [{ from: delay, to: horizonMs }] : []
  const windows: VisibilityWindow[] = []
  for (let start = delay; start < horizonMs && windows.length < 200; start += repeatEveryMs || Infinity) {
    windows.push({ from: start, to: Math.min(horizonMs, start + visibleForMs) })
    if (!repeatEveryMs) break
  }
  return windows
}

/** L'element est-il a l'antenne `ms` apres l'apparition de l'habillage ? */
export function elementVisibleAt(element: ElementTiming, ms: number): boolean {
  return visibilityWindows(element, ms + 1).some(window => ms >= window.from && ms < window.to)
}
